import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import CompanySelect from '@/pages/CompanySelect';
import LegalDashboard from '@/pages/LegalDashboard';
import CompanyDashboard from '@/pages/CompanyDashboard';
import ExecutiveDashboard from '@/pages/ExecutiveDashboard';
import ContractDetails from '@/pages/ContractDetails';
import CaseDetails from '@/pages/CaseDetails';
import CommitmentDetails from '@/pages/CommitmentDetails';
import AgreementDetails from '@/pages/AgreementDetails';
import Analytics from '@/pages/Analytics';
import Search from '@/pages/Search';
import Profile from '@/pages/Profile';
import ExecutionDashboard from '@/pages/ExecutionDashboard';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function PrivateRoute({ children, allowedRole }) {
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');

  if (!token) {
    return <Navigate to="/login" />;
  }

  if (allowedRole && userRole !== allowedRole) {
    return <Navigate to="/" />;
  }

  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`)
        .then(response => {
          setUser(response.data);
          localStorage.setItem('userRole', response.data.role);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-slate-600">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="App" dir="rtl">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                {user?.role === 'legal_manager' ? (
                  <Navigate to="/companies" />
                ) : (
                  <Navigate to="/executive" />
                )}
              </PrivateRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <PrivateRoute allowedRole="legal_manager">
                <CompanySelect user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/legal"
            element={
              <PrivateRoute allowedRole="legal_manager">
                <LegalDashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/legal/company/:companyId"
            element={
              <PrivateRoute allowedRole="legal_manager">
                <CompanyDashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/executions/:companyId"
            element={
              <PrivateRoute>
                <ExecutionDashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/executive"
            element={
              <PrivateRoute allowedRole="executive_manager">
                <ExecutiveDashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/contract/:id"
            element={
              <PrivateRoute>
                <ContractDetails user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/case/:id"
            element={
              <PrivateRoute>
                <CaseDetails user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/commitment/:id"
            element={
              <PrivateRoute>
                <CommitmentDetails user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/agreement/:id"
            element={
              <PrivateRoute>
                <AgreementDetails user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute allowedRole="legal_manager">
                <Analytics user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/:companyId"
            element={
              <PrivateRoute allowedRole="legal_manager">
                <Analytics user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/search"
            element={
              <PrivateRoute>
                <Search user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile user={user} setUser={setUser} />
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" dir="rtl" />
    </div>
  );
}

export default App;